import { Box } from "@mui/material";
import { isFulfilled, nanoid, PayloadAction } from "@reduxjs/toolkit";
import { isValid } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { convertDateToYYYYMMDDString } from "../../../../app/common";
import { NO_DRAG_SELECTOR } from "../../../../app/constants";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { ConfirmationDialog } from "../../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import useBackendPolling from "../../../../hooks/useBackendPolling";
import useMessage from "../../../../hooks/useMessage";
import { fetchAssignmentsByTalentId } from "../../../talentProfile/Employment/AssignmentAdditionalInfo/AssignmentsSlice";
import { AssignmentById } from "../../../talents/talentAPI";
import { fetchAssignmentById } from "../../../talents/talentsSlice";
import { isExpertAdmin } from "../../../user/accessRightChecks";
import { WORKTIME_BANK_LEAVE_ABSENCE_TYPE } from "../../constants";
import {
  Entry,
  Leave,
  LeaveEntity,
  LeaveEntityDateRange,
  LeaveEntry,
  LeaveMap,
  NewEntryBase,
  NewEntryTalentData,
  removeRootIdFromLeaveMap,
} from "../../entriesAPI";
import {
  doUploadLeaveAttachmentToS3,
  fetchAbsencesById,
  fetchEntriesAbsence,
  fetchLeaveEntryById,
  getLeaveAttachmentUploadUrl,
  selectAllAbsenceEntries,
  updateEntriesLeaveAttachment,
} from "../../entriesSlice";
import {
  assignmentsAvailableForEntry,
  buildLeaveEntity,
  disableApproveButton,
  disableSaveButton,
} from "../Shared/functions";
import Top from "../Top";
import AbsenceEdit from "./AbsenceEdit/AbsenceEdit";
import AbsenceHoursList, {
  AbsenceListEntry,
} from "./AbsenceHoursList/AbsenceHoursList";
import { SICK_LEAVE_TYPES } from "./Absences";
import {
  getAbsenceHistoryFromDate,
  getAbsenceWithContinuation,
} from "./helpers";
import GeneralDetails from "./LeaveDetailsComponents/GeneralDetails";
import SickLeaveDetails from "./LeaveDetailsComponents/SickLeaveDetails";
import SaveAndApproveButtons from "./SaveAndApproveButtons";

type Props = {
  selectedEntry: Entry;
  cancel: () => void;
  leaveEntity: LeaveEntity;
  setLeaveEntity: (leaveEntity: LeaveEntity) => void;
  updateLeaveEntity: (
    key: string,
    value: string | LeaveEntityDateRange | null | Leave,
  ) => void;
  originalLeaveEntity: LeaveEntity | null;
  setOriginalLeaveEntity: (leaveEntity: LeaveEntity) => void;
  clearLeaveEntity: () => void;
  translatedEntryTypes: { [key: string]: string };
  handleConfirmSave: (
    markSelectedAsApproved: boolean,
    entries: AbsenceListEntry[],
    versionOverride?: number,
    isAddingAttachment?: boolean,
  ) => void;
  absenceToEdit: AbsenceListEntry | null;
  setAbsenceToEdit: (entry: AbsenceListEntry | null) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  newEntryTalentData?: NewEntryTalentData;
};

const LeaveMain = ({
  selectedEntry,
  cancel,
  leaveEntity,
  setLeaveEntity,
  updateLeaveEntity,
  originalLeaveEntity,
  setOriginalLeaveEntity,
  translatedEntryTypes,
  handleConfirmSave,
  absenceToEdit,
  setAbsenceToEdit,
  isLoading,
  setIsLoading,
  newEntryTalentData,
}: Props) => {
  const talentId = newEntryTalentData?.id;

  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const canBypassAttachmentCheck = !!user && isExpertAdmin(user);
  const absenceEntries = useAppSelector(selectAllAbsenceEntries);
  const prevDateRangeRef = useRef<LeaveEntityDateRange>({
    startDate: null,
    endDate: null,
  });
  const [fetchingDone, setFetchingDone] = useState<boolean>(false);
  const [entries, setEntries] = useState<AbsenceListEntry[]>([]);
  const [originalEntries, setOriginalEntries] = useState<AbsenceListEntry[]>(
    [],
  );
  const [inputErrors, setInputErrors] = useState<{ [code: string]: boolean }>(
    {},
  );
  const [assignments, setAssignments] = useState<AssignmentById[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    markSelectedAsApproved: boolean;
  }>({ isOpen: false, markSelectedAsApproved: false });
  const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
  const [isProcessingUpload, setIsProcessingUpload] = useState(false);
  // Prevents re-uploading the same files if save is retried after a polling timeout
  const [isUploadedAwaitingConfirmation, setIsUploadedAwaitingConfirmation] =
    useState(false);

  const { t } = useTranslation();
  const { sendMessage } = useMessage();

  useEffect(() => {
    if (selectedEntry.leaveId) {
      setIsLoading(true);

      dispatch(fetchLeaveEntryById(selectedEntry.leaveId)).then((action) => {
        const responseData = action.payload;
        const entryData = responseData as LeaveEntry;

        const fetchedLeaveEntity = buildLeaveEntity(
          selectedEntry.talentId,
          entryData,
        );
        setLeaveEntity({ ...fetchedLeaveEntity });
        setOriginalLeaveEntity({ ...fetchedLeaveEntity });
      });
    }
    // Might cause a loop if not disabled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntry.id, dispatch, selectedEntry.leaveId]);

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      const foundTalentId = selectedEntry?.talentId || talentId;

      if (
        leaveEntity?.dateRange?.startDate &&
        leaveEntity?.dateRange?.endDate &&
        foundTalentId &&
        prevDateRangeRef.current !== leaveEntity.dateRange
      ) {
        try {
          const startDate = new Date(leaveEntity.dateRange.startDate);
          startDate.setHours(0, 0, 0, 0); // Set time to start of the day (00:00)

          const endDate = new Date(leaveEntity.dateRange.endDate);
          endDate.setDate(endDate.getDate() + 1); // Extend to next day at 00:00:00
          endDate.setHours(0, 0, 0, 0); // Set time to 00:00 of the next day

          // If end date is not valid, don't try to fetch
          if (!isValid(new Date(leaveEntity.dateRange.endDate))) {
            setIsLoading(false);
            setFetchingDone(false);
            return;
          }

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const [entriesResult, absencesResult, assignmentsResult] =
            await Promise.all([
              dispatch(
                fetchEntriesAbsence({
                  sort: "TALENT",
                  pageSize: "500",
                  rangeStart: startDate.getTime().toString(),
                  rangeEnd: endDate.getTime().toString(),
                  talentId: foundTalentId,
                }),
              ),
              dispatch(
                fetchAbsencesById({
                  talentId: foundTalentId,
                  type: leaveEntity.type,
                  from: getAbsenceHistoryFromDate(
                    leaveEntity.dateRange.startDate,
                  ),
                  to: leaveEntity.dateRange.startDate,
                }),
              ) as Promise<PayloadAction<LeaveMap>>, // Cast the payload to the expected type
              dispatch(
                fetchAssignmentsByTalentId({
                  talentId: foundTalentId,
                  start: leaveEntity.dateRange.startDate,
                  end: leaveEntity.dateRange.endDate,
                }),
              ),
            ]);

          prevDateRangeRef.current = leaveEntity.dateRange;

          // Take the current one away from previous leaves
          const previousLeaves = leaveEntity.id
            ? removeRootIdFromLeaveMap(absencesResult.payload, leaveEntity.id)
            : absencesResult.payload;
          const updatedLeaveEntity = {
            ...leaveEntity,
            previousLeaves: previousLeaves,
            continuationOf: leaveEntity.continuationOf,
            continuationOfId: leaveEntity.continuationOfId,
          };

          setLeaveEntity({ ...updatedLeaveEntity });
          setAssignments(assignmentsResult.payload as AssignmentById[]);
          setIsLoading(false);
          setFetchingDone(true);
        } catch (error) {
          console.error("Error fetching data", error);
          setIsLoading(false);
          setFetchingDone(true);
        }
      } else {
        setIsLoading(false);
        setFetchingDone(false);
      }
    };
    setFetchingDone(false);
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    leaveEntity?.dateRange?.startDate,
    leaveEntity?.dateRange?.endDate,
    selectedEntry?.talentId,
    talentId,
  ]);

  // Fetch previous leaves when leave type changes so that "can continue previous" can be handled
  useEffect(() => {
    const foundTalentId = selectedEntry?.talentId || talentId;
    if (!leaveEntity || !leaveEntity.dateRange.startDate || !foundTalentId)
      return;
    setIsLoading(true);
    dispatch(
      fetchAbsencesById({
        talentId: foundTalentId,
        type: leaveEntity.type,
        from: getAbsenceHistoryFromDate(leaveEntity.dateRange.startDate),
        to: leaveEntity.dateRange.startDate,
      }),
    ).then((absencesResult) => {
      if (isFulfilled(absencesResult)) {
        const newEnt = getAbsenceWithContinuation(
          absencesResult.payload,
          leaveEntity,
        );
        setLeaveEntity(newEnt);
      }
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    leaveEntity.type,
    dispatch,
    selectedEntry?.talentId,
    talentId,
    setLeaveEntity,
  ]);

  // Helper function to generate a new entry for days without any existing absence entry
  const createNewEntry = useCallback(
    (date: Date, talentId: string): AbsenceListEntry => {
      let newEntry: NewEntryBase = {
        created: Date.now(),
        checked: false,
        id: nanoid(),
        assignmentId: "",
        approvalState: "New",
        start: new Date(date).setHours(8, 0, 0, 0), // Start time at 8:00 AM
        end: new Date(date).setHours(16, 0, 0, 0), // End time at 4:00 PM
        version: 0,
        customerOrgId: "",
        customerOrgName: "",
        customerCostCenterId: "",
        customerCostCenterName: "",
        leaveId: null,
        isShift: false,
        type: leaveEntity?.type || "WORK_ENTRY",
        defaultLunchBreak: null,
        lunchBreak: null,
        plannedStart: 0,
        feedback: null,
        talentId,
        talentFirstName: newEntryTalentData?.firstName || "",
        talentLastName: newEntryTalentData?.lastName || "",
        expenses: {},
        isBillable: true,
        workLocationName: "",
        workLocationId: "",
        workTitleId: "",
        approvalStateChanges: [],
        entryChanges: [],
        supplements: [],
        comment: "",
      };

      // Assignment handling remains unchanged
      const availableAssignments: AssignmentById[] =
        assignmentsAvailableForEntry(assignments, newEntry as AbsenceListEntry);
      if (availableAssignments.length === 1) {
        newEntry.assignmentId = availableAssignments[0].assignmentId;

        const costCenterOptions =
          availableAssignments[0].details.custCostCenters;
        if (costCenterOptions.length === 1) {
          newEntry.customerCostCenterId = costCenterOptions[0].id;
          newEntry.customerCostCenterName = costCenterOptions[0].name;
        }
      }

      // For long assignments, check if default lunch break exists
      if (newEntry.assignmentId) {
        dispatch(fetchAssignmentById(newEntry.assignmentId)).then((action) => {
          if (isFulfilled(action)) {
            if (
              !action.payload.isShift &&
              action.payload.details.defaultLunchBreak
            ) {
              const { defaultLunchBreak } = action.payload.details;
              newEntry.defaultLunchBreak = defaultLunchBreak;
              if (
                defaultLunchBreak.setAutomaticallyAfter &&
                defaultLunchBreak.setAutomaticallyAfter <= 480
              ) {
                const startingDeltaMs = defaultLunchBreak.startDelta * 60000;
                const lunchBreak = {
                  start: newEntry.start + startingDeltaMs,
                  duration: defaultLunchBreak.length,
                };
                newEntry.lunchBreak = lunchBreak;

                // New entry has been set to state at this point, so
                // update lunch break for it after assignment has been fetched
                setEntries((prevEntries) =>
                  prevEntries.map((entry) =>
                    entry.id === newEntry.id
                      ? { ...entry, defaultLunchBreak, lunchBreak }
                      : entry,
                  ),
                );
              }
            }
          }
        });
      }

      return newEntry as AbsenceListEntry;
    },
    [assignments, leaveEntity?.type, newEntryTalentData, dispatch, setEntries],
  );

  useEffect(() => {
    // If still fetching, return early
    if (!fetchingDone) return;
    const typesThatSkipSunday = ["ANNUAL_HOLIDAY"];

    if (leaveEntity?.dateRange?.startDate && leaveEntity?.dateRange?.endDate) {
      const startDate = leaveEntity.dateRange.startDate;
      const endDate = leaveEntity.dateRange.endDate;

      const allDates = [];

      // Normalize startDate and endDate as date strings (yyyy-mm-dd)
      const normalizedStartDate = new Date(startDate);
      const normalizedEndDate = new Date(endDate);

      // Loop through each day between normalizedStartDate and normalizedEndDate
      for (
        let date = new Date(normalizedStartDate);
        date <= normalizedEndDate;
        date.setDate(date.getDate() + 1)
      ) {
        // Skip Sundays if needed
        if (
          typesThatSkipSunday.includes(leaveEntity.type) &&
          date.getDay() === 0
        )
          continue;

        const dateString = convertDateToYYYYMMDDString(date, "-");
        allDates.push(dateString); // Collect the date string
      }

      const allEntries: AbsenceListEntry[] = [];
      const existingEntriesByStartDate: {
        [dateString: string]: AbsenceListEntry[];
      } = {};

      // Organize existing entries by start date (for quick lookup)
      absenceEntries.forEach((entry: Entry) => {
        const entryStartDate = new Date(entry.start);
        const startDateString = convertDateToYYYYMMDDString(
          entryStartDate,
          "-",
        );

        if (!existingEntriesByStartDate[startDateString]) {
          existingEntriesByStartDate[startDateString] = [];
        }
        existingEntriesByStartDate[startDateString].push(
          entry as AbsenceListEntry,
        ); // Add the entry under its start date
      });

      // Iterate over allDates and check if an entry already exists for each date
      allDates.forEach((dateString) => {
        const entriesForDate = existingEntriesByStartDate[dateString];
        let entryFound = false;

        if (entriesForDate) {
          // For each existing entry, add it to allEntries if it matches the leaveEntity or doesn't have a leaveId
          entriesForDate.forEach((existingEntry) => {
            if (
              !existingEntry.leaveId ||
              existingEntry.leaveId === leaveEntity.id
            ) {
              entryFound = true;
              allEntries.push({
                ...existingEntry,
                checked: Boolean(
                  leaveEntity.id && existingEntry.leaveId === leaveEntity.id,
                ),
              });
            }
          });
        }

        // If no valid entry was found for this date, create a new empty entry
        if (!entryFound && talentId) {
          const newEntry = createNewEntry(new Date(dateString), talentId); // Convert string back to Date
          allEntries.push(newEntry);
        }
      });

      // Set the new entries in the state
      setEntries(allEntries);
      setOriginalEntries(allEntries);
    } else {
      // Fallback to default absence entries if no date range
      setEntries(absenceEntries as AbsenceListEntry[]);
      setOriginalEntries(absenceEntries as AbsenceListEntry[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    absenceEntries,
    leaveEntity?.dateRange?.startDate,
    leaveEntity?.dateRange?.endDate,
    talentId,
    assignments,
  ]);

  const handleCancel = () => {
    setIsLoading(false);
    cancel();
  };

  const pendingApproveRef = useRef<boolean>(false);
  const expectedAttachmentCountRef = useRef<number>(0);

  const {
    startPolling: startAttachmentPolling,
    stopPolling: stopAttachmentPolling,
    isTimeout: isAttachmentPollingTimeout,
  } = useBackendPolling({
    callback: async () => {
      if (!selectedEntry.leaveId) return;
      const action = await dispatch(fetchLeaveEntryById(selectedEntry.leaveId));
      if (!isFulfilled(action)) return;

      const entryData = action.payload as LeaveEntry;
      const currentCount =
        entryData.entries[0]?.leaveAttachment?.attachments?.length ?? 0;
      const allUploaded = currentCount >= expectedAttachmentCountRef.current;

      if (allUploaded) {
        dispatch(
          updateEntriesLeaveAttachment(
            entryData.entries.map((e) => ({
              id: e.id,
              leaveAttachment: e.leaveAttachment,
            })),
          ),
        );
        setLeaveEntity({
          ...buildLeaveEntity(selectedEntry.talentId, entryData),
        });
        stopAttachmentPolling();
        handleConfirmSave(
          pendingApproveRef.current,
          entries,
          entryData.version,
          true,
        );
        setPendingAttachments([]);
        setIsUploadedAwaitingConfirmation(false);
        setIsProcessingUpload(false);
      }
    },
    maxAttempts: 20,
    delay: 3000,
  });

  useEffect(() => {
    if (isAttachmentPollingTimeout) {
      setIsProcessingUpload(false);
      sendMessage(t("hourEntries:attachmentUploadTimeoutRetry"), "error");
    }
    // sendMessage as dependency creates infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAttachmentPollingTimeout, t]);

  const performSave = useCallback(
    async (markSelectedAsApproved: boolean) => {
      if (
        pendingAttachments.length > 0 &&
        leaveEntity.id &&
        leaveEntity.talentId
      ) {
        setIsProcessingUpload(true);

        if (!isUploadedAwaitingConfirmation) {
          for (const attachment of pendingAttachments) {
            const urlAction = await dispatch(
              getLeaveAttachmentUploadUrl({
                talentId: leaveEntity.talentId,
                leaveId: leaveEntity.id,
                filename: attachment.name,
                size: String(attachment.size),
              }),
            );
            if (!isFulfilled(urlAction)) {
              setIsProcessingUpload(false);
              return;
            }

            const uploadAction = await dispatch(
              doUploadLeaveAttachmentToS3({
                url: urlAction.payload.url,
                file: attachment,
              }),
            );
            if (!isFulfilled(uploadAction)) {
              setIsProcessingUpload(false);
              return;
            }
          }
          setIsUploadedAwaitingConfirmation(true);
        }

        if (selectedEntry.leaveId) {
          expectedAttachmentCountRef.current =
            (leaveEntity.leaveAttachment?.attachments?.length ?? 0) +
            pendingAttachments.length;
          pendingApproveRef.current = markSelectedAsApproved;
          startAttachmentPolling();
        } else {
          handleConfirmSave(markSelectedAsApproved, entries);
          setPendingAttachments([]);
          setIsProcessingUpload(false);
        }
      } else {
        handleConfirmSave(markSelectedAsApproved, entries);
      }
    },
    [
      pendingAttachments,
      isUploadedAwaitingConfirmation,
      leaveEntity.id,
      leaveEntity.talentId,
      leaveEntity.leaveAttachment?.attachments?.length,
      selectedEntry.leaveId,
      dispatch,
      entries,
      handleConfirmSave,
      startAttachmentPolling,
    ],
  );

  const handleSave = (markSelectedAsApproved: boolean) => {
    if (markSelectedAsApproved) {
      setConfirmDialog({ isOpen: true, markSelectedAsApproved });
    } else {
      void performSave(false);
    }
  };

  const handleCheckboxUpdate = useCallback(
    (entryId: string, checked: boolean) => {
      // Create a direct update on the existing entries state
      setEntries((prevEntries) => {
        const updatedEntryIndex = prevEntries.findIndex(
          (entry) => entry.id === entryId,
        );

        // If entry is found, update only that entry
        if (updatedEntryIndex !== -1) {
          const originalEntry = originalEntries[updatedEntryIndex]; // Find original entry once to avoid search every time

          const updatedEntry = {
            ...prevEntries[updatedEntryIndex],
            checked: checked,
            start: checked
              ? prevEntries[updatedEntryIndex].start
              : originalEntry?.start || prevEntries[updatedEntryIndex].start,
            end: checked
              ? prevEntries[updatedEntryIndex].end
              : originalEntry?.end || prevEntries[updatedEntryIndex].end,
          };

          // If the object reference hasn't changed, don't update
          if (
            prevEntries[updatedEntryIndex].checked === updatedEntry.checked &&
            prevEntries[updatedEntryIndex].start === updatedEntry.start &&
            prevEntries[updatedEntryIndex].end === updatedEntry.end
          ) {
            return prevEntries; // No changes, return previous state
          }

          // Return a new array with only the specific entry updated
          const newEntries = [...prevEntries];
          newEntries[updatedEntryIndex] = updatedEntry;

          return newEntries;
        }

        return prevEntries; // No update
      });
    },
    [originalEntries],
  );

  const updateEntry = (updatedEntry: AbsenceListEntry) => {
    const id = updatedEntry.id;

    setEntries((prevEntries) => {
      const newEntries = prevEntries.map((entry) => {
        // Only replace the entry if it has changed, else return the same reference
        if (entry.id === id && entry !== updatedEntry) {
          return updatedEntry;
        }
        return entry;
      });

      return newEntries;
    });
  };

  const handleCheckAllToggle = (checked: boolean) => {
    const updatedEntries = entries.map((entry) => {
      // Filter assignments based on the entry's start and end time
      const assignmentsForEntryDate: AssignmentById[] =
        assignmentsAvailableForEntry(assignments, entry);
      // Only update `checked` if there are available assignments
      // and the entry is not a shift if the leave type is WORKTIME_BANK_LEAVE
      const allowedToCheck =
        leaveEntity.type === WORKTIME_BANK_LEAVE_ABSENCE_TYPE
          ? !entry.isShift
          : true;
      if (
        (assignmentsForEntryDate?.length > 0 || !talentId) &&
        allowedToCheck
      ) {
        return {
          ...entry,
          checked, // Update the `checked` field only if there are assignments or no talentId (in talent view)
        };
      }
      // Return the entry unchanged
      return entry;
    });

    // Set the updated entries state
    setEntries(updatedEntries);
  };

  // Create a new entry row for the selected date
  const createNewEntryRow = (start: number) => {
    const newEntry = createNewEntry(new Date(start), talentId!);
    setEntries([...entries, newEntry]);
  };

  const detailsSelector = (leaveEntityType: string) => {
    if (SICK_LEAVE_TYPES.includes(leaveEntityType)) {
      return (
        <SickLeaveDetails
          selectedEntry={selectedEntry}
          leaveEntity={leaveEntity}
          setLeaveEntity={setLeaveEntity}
          updateLeaveEntity={updateLeaveEntity}
          editingActive={absenceToEdit ? true : false}
          translatedEntryTypes={translatedEntryTypes}
          inputErrors={inputErrors}
          setInputErrors={setInputErrors}
          entries={entries}
          setEntries={setEntries}
          pendingAttachments={pendingAttachments}
          onPendingAttachmentsChange={setPendingAttachments}
        />
      );
    }

    /* HIDDEN FOR NOW, (discussed in meeting 3.10.2024) AS EVERY OTHER TYPE IS USING GENERAL DETAILS UNTIL BE SUPPPOT IS ADDED
                                else if (leaveEntityType === "ANNUAL_HOLIDAY") {
                                  return (
                                    <AnnualDetails
                                      selectedEntry={selectedEntry}
                                      leaveEntity={leaveEntity}
                                      updateLeaveEntity={updateLeaveEntity}
                                      editingActive={absenceToEdit ? true : false}
                                      translatedEntryTypes={translatedEntryTypes}
                                      inputErrors={inputErrors}
                                      setInputErrors={setInputErrors}
                                    />
                                  );
                                }
                                */

    // Default case for all other types
    return (
      <GeneralDetails
        selectedEntry={selectedEntry}
        leaveEntity={leaveEntity}
        setLeaveEntity={setLeaveEntity}
        updateLeaveEntity={updateLeaveEntity}
        editingActive={absenceToEdit ? true : false}
        translatedEntryTypes={translatedEntryTypes}
        inputErrors={inputErrors}
        setInputErrors={setInputErrors}
        entries={entries}
        setEntries={setEntries}
        pendingAttachments={pendingAttachments}
        onPendingAttachmentsChange={setPendingAttachments}
      />
    );
  };

  // Memoize sorted entries
  const sortedEntries: AbsenceListEntry[] = useMemo(() => {
    return [...entries].sort((ent1, ent2) => ent1.start! - ent2.start!);
  }, [entries]);

  // Memoize assignments
  const memoizedAssignments = useMemo(() => assignments, [assignments]);

  return (
    selectedEntry && (
      <Box
        sx={{
          minWidth: "1329px",
          maxWidth: "1400px",
          maxHeight: "95vh",
          overflowX: "hidden",
          padding: "0px 15px 15px",
        }}
      >
        <OverlaySpinner isLoading={isLoading || isProcessingUpload} />
        <Top
          entry={selectedEntry}
          newEntryTalentData={newEntryTalentData}
          handleCancel={handleCancel}
        />
        <Box
          className={NO_DRAG_SELECTOR}
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          {/* Details component based on type of leave */}
          {leaveEntity?.type && detailsSelector(leaveEntity.type)}
          {absenceToEdit ? (
            <AbsenceEdit
              entryToEdit={absenceToEdit}
              setEntryToEdit={setAbsenceToEdit}
              entries={sortedEntries}
              setEntries={setEntries}
              inputErrors={inputErrors}
              setInputErrors={setInputErrors}
            />
          ) : (
            <AbsenceHoursList
              sortedEntries={sortedEntries}
              setEntries={setEntries}
              setEntryToEdit={setAbsenceToEdit}
              handleCheckboxUpdate={handleCheckboxUpdate}
              absenceType={leaveEntity.type}
              inputErrors={inputErrors}
              setInputErrors={setInputErrors}
              talentId={talentId}
              assignments={memoizedAssignments}
              updateEntry={updateEntry}
              createNewEntryRow={createNewEntryRow}
              handleCheckAllToggle={handleCheckAllToggle}
            />
          )}
        </Box>
        <SaveAndApproveButtons
          handleSave={handleSave}
          disableApprove={
            isProcessingUpload ||
            disableApproveButton(
              entries,
              inputErrors,
              leaveEntity,
              pendingAttachments.length > 0,
              canBypassAttachmentCheck,
            )
          }
          saveDisabled={
            isProcessingUpload ||
            disableSaveButton(
              leaveEntity,
              originalLeaveEntity,
              inputErrors,
              entries,
              originalEntries,
              pendingAttachments.length > 0,
            )
          }
        />
        <ConfirmationDialog
          title={t("hourEntries:absenceEntriesApprovalConfirmationTitle")}
          contentText={t(
            "hourEntries:absenceEntriesApprovalConfirmationContext",
          )}
          confirmText={t("hourEntries:entriesConfirmButton")}
          cancelText={t("hourEntries:entriesCancelButton")}
          isOpen={confirmDialog.isOpen}
          onCancel={() =>
            setConfirmDialog({ isOpen: false, markSelectedAsApproved: false })
          }
          onConfirm={() => {
            setConfirmDialog({ isOpen: false, markSelectedAsApproved: false });
            void performSave(true);
          }}
        />
      </Box>
    )
  );
};

export default LeaveMain;
